import axios from '@/api/axios';
import type {
  BusinessRFI,
  BusinessRFIListResponse,
  BusinessRFINote,
  BusinessRFISource,
  BusinessRFIStatus,
  CreateBusinessRFIPayload,
  UpdateBusinessRFIPayload,
} from '@/types';

const BASE_URL = '/v2/admin/business/rfi';

/**
 * What the create/get/update endpoints actually put on the wire.
 *
 * These three routes are response-minimized to ADMIN_RFI_DETAIL_FIELDS, so the
 * full record does NOT arrive even though the server's handler returns one.
 * Typing them as the full RFI made `const {status} = await updateBusinessRFI()`
 * compile cleanly and be undefined at runtime; this is the shape the boundary
 * really allows. Everything else the RFI screens render comes from the list.
 *
 * `notes` no longer lives on `BusinessRFI` (the list-item type): the list
 * endpoint never sends it, only this detail shape does, so it is added back
 * here directly rather than picked off `BusinessRFI`.
 */
export type BusinessRFIDetail = Pick<
  BusinessRFI,
  'id' | 'businessId' | 'customerEmail'
> & {notes: BusinessRFINote[]};

export const rfiService = {
  getBusinessRFIList: async (
    businessId: string,
    filters: {
      status?: BusinessRFIStatus;
      customerEmail?: string;
      transactionId?: string;
      source?: BusinessRFISource;
      page?: number;
      limit?: number;
    } = {},
  ): Promise<BusinessRFIListResponse> => {
    const query: Record<string, string> = {
      businessId,
      page: String(filters.page ?? 0),
      limit: String(filters.limit ?? 10),
    };
    if (filters.status) {
      query.status = filters.status;
    }
    if (filters.customerEmail) {
      query.customerEmail = filters.customerEmail;
    }
    if (filters.transactionId) {
      query.transactionId = filters.transactionId;
    }
    if (filters.source) {
      query.source = filters.source;
    }
    const url = `${BASE_URL}?${new URLSearchParams(query).toString()}`;
    const response = await axios.get<BusinessRFIListResponse>(url);
    return response?.data;
  },

  getBusinessRFI: async (rfiId: string): Promise<BusinessRFIDetail> => {
    const response = await axios.get<BusinessRFIDetail>(`${BASE_URL}/${rfiId}`);
    return response?.data;
  },

  createBusinessRFI: async (
    payload: CreateBusinessRFIPayload,
  ): Promise<BusinessRFIDetail> => {
    const response = await axios.post<BusinessRFIDetail>(BASE_URL, payload);
    return response?.data;
  },

  updateBusinessRFI: async (
    rfiId: string,
    payload: UpdateBusinessRFIPayload,
  ): Promise<BusinessRFIDetail> => {
    const response = await axios.patch<BusinessRFIDetail>(
      `${BASE_URL}/${rfiId}`,
      payload,
    );
    return response?.data;
  },
};
